"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import styled, { keyframes } from "styled-components"
import { User, Settings, MapPin, Calendar, Link as LinkIcon, Camera, Heart, MessageSquare, Share, MoreHorizontal, Grid, List, Users, Loader, X, Check, Mail } from "lucide-react"
import Cropper from "react-easy-crop"
import PostCard from "./PostCard"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useParams, useNavigate } from "react-router-dom"
import SwipeMatching from "../components/SwipeMatching"
import PeopleYouMayKnow from "../components/PeopleYouMayKnow"

// 자른 영역 픽셀 좌표 + 원본 이미지 src로 cropped Blob 생성
async function getCroppedBlob(imageSrc, pixelCrop, fileType = 'image/jpeg') {
  const image = await new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = imageSrc
  })

  const canvas = document.createElement('canvas')
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height
  const ctx = canvas.getContext('2d')

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  )

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), fileType, 0.9)
  })
}

const Container = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 12px;
  }
`

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const SidePanel = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 88px;

  @media (max-width: 900px) {
    position: static;
  }
`

const WidgetCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadow};
  padding: 16px 18px;
`

const WidgetTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  letter-spacing: 0.2px;
`

const WidgetMetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
  color: ${props => props.theme.textSecondary};

  & > div {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  a {
    color: ${props => props.theme.buttonPrimary};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`

const WidgetStatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  text-align: center;
`

const WidgetStatBox = styled.button`
  background: none;
  border: none;
  padding: 8px 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }

  .num {
    font-size: 18px;
    font-weight: 700;
    color: ${props => props.theme.textPrimary};
  }
  .label {
    margin-top: 2px;
    font-size: 11px;
    color: ${props => props.theme.textSecondary};
  }
`

const CropOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`

const CropModal = styled.div`
  background: #1f2937;
  color: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
`

const CropModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  font-size: 14px;
  font-weight: 600;
`

const CropModalClose = styled.button`
  background: transparent;
  border: none;
  color: #cbd5e1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 6px;
  &:hover { background: rgba(255,255,255,0.1); color: #fff; }
`

const CropArea = styled.div`
  position: relative;
  width: 100%;
  height: 360px;
  background: #000;
`

const CropControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-top: 1px solid rgba(255,255,255,0.1);
`

const CropZoomLabel = styled.span`
  font-size: 12px;
  color: #94a3b8;
  min-width: 32px;
`

const CropZoomSlider = styled.input`
  flex: 1;
  accent-color: #3b82f6;
`

const CropActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px 18px;
`

const CropCancelBtn = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.2);
  background: transparent;
  color: #e2e8f0;
  font-size: 13px;
  cursor: pointer;
  &:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const CropConfirmBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: #3b82f6;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #2563eb; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

// === 통합 ProfileShell: 배너+프로필이 하나의 카드 ===
const HeroWrap = styled.div`
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow};
  background-color: ${props => props.theme.cardBackground};
`

const HeroBanner = styled.div`
  width: 100%;
  height: 165px;
  position: relative;
  overflow: hidden;
  background-image: ${props => props.$cover
    ? `url(${props.$cover})`
    : `linear-gradient(110deg, #b9c9f5 0%, #c5cef0 35%, #d6c5ec 70%, #e3c3df 100%)`};
  background-size: cover;
  background-position: center;

  /* 우측 흐르는 흰 곡선 (S-wave 느낌) */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 90% at 95% 30%, rgba(255,255,255,0.45) 0%, transparent 55%),
      radial-gradient(ellipse 55% 80% at 80% 70%, rgba(255,255,255,0.35) 0%, transparent 60%),
      radial-gradient(ellipse 40% 60% at 60% 40%, rgba(255,255,255,0.18) 0%, transparent 70%);
    pointer-events: none;
  }

  /* 별 dot */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 72% 28%, rgba(255,255,255,0.85) 1.2px, transparent 1.8px),
      radial-gradient(circle at 88% 50%, rgba(255,255,255,0.8) 1px, transparent 1.6px),
      radial-gradient(circle at 65% 75%, rgba(255,255,255,0.7) 1px, transparent 1.6px),
      radial-gradient(circle at 92% 80%, rgba(255,255,255,0.6) 1px, transparent 1.6px);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    height: 125px;
  }
`

const ProfileMoreMenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13.5px;
  color: ${p => p.$danger ? '#dc2626' : '#1f2937'};
  &:hover { background: ${p => p.$danger ? '#fef2f2' : '#f3f4f6'}; }
  & + & { border-top: 1px solid #f3f4f6; }
`

const BannerMoreBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  color: #4b5563;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  z-index: 3;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

  &:hover { background: #ffffff; color: #1f2937; }
`

const HeroLogoOverlay = styled.div`
  position: absolute;
  left: 28px;
  top: 26px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
  font-weight: 700;
  font-size: 22px;
  letter-spacing: 2px;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);

  &::before {
    content: '';
    width: 28px;
    height: 32px;
    background: rgba(255, 255, 255, 0.92);
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    flex-shrink: 0;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
    line-height: 1;
  }

  small {
    font-size: 9px;
    font-weight: 500;
    opacity: 0.85;
    letter-spacing: 1.4px;
    margin-top: 4px;
    display: block;
  }

  @media (max-width: 768px) {
    left: 16px;
    top: 18px;
    font-size: 16px;

    &::before { width: 22px; height: 26px; }
  }
`

const ProfileFlat = styled.div`
  padding: 24px 28px 26px;
  display: grid;
  grid-template-columns: 155px minmax(0, 1fr) 150px;
  gap: 28px;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 18px;
  }
`

const FloatingPhone = styled.div`
  width: 155px;
  height: 280px;
  border-radius: 22px;
  position: relative;
  background-image: ${props => props.$cover
    ? `url(${props.$cover})`
    : `
      radial-gradient(circle at 22% 24%, rgba(255,255,255,0.9) 1.4px, transparent 2.2px),
      radial-gradient(circle at 76% 16%, rgba(255,255,255,0.75) 1.2px, transparent 2px),
      radial-gradient(circle at 82% 48%, rgba(255,255,255,0.8) 1.3px, transparent 2.1px),
      radial-gradient(circle at 30% 62%, rgba(255,255,255,0.65) 1px, transparent 1.8px),
      radial-gradient(circle at 86% 78%, rgba(255,255,255,0.7) 1.2px, transparent 2px),
      radial-gradient(circle at 18% 88%, rgba(255,255,255,0.55) 1px, transparent 1.7px),
      radial-gradient(ellipse 80% 60% at 90% 25%, rgba(255,255,255,0.22) 0%, transparent 60%),
      radial-gradient(ellipse 60% 45% at 15% 80%, rgba(255,255,255,0.18) 0%, transparent 65%),
      linear-gradient(160deg, #8e9efb 0%, #a89bea 50%, #b39ce6 100%)
    `};
  background-size: cover;
  background-position: center;
  border: 5px solid #ffffff;
  box-shadow: 0 14px 32px rgba(76, 91, 184, 0.28);
  margin-top: -110px;
  z-index: 2;
  overflow: hidden;
  background-clip: padding-box;

  @media (max-width: 768px) {
    margin: -70px auto 0;
  }
`

const PhoneWatermark = styled.div`
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.85);
  pointer-events: none;

  &::before {
    content: '';
    width: 22px;
    height: 26px;
    background: rgba(255, 255, 255, 0.9);
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  }

  .text {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.6px;
  }
`

const FloatingPhoneInner = styled.div`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 8px 10px;
`

const FloatingPhoneAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${props => props.theme.borderLight};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
`

const FloatingPhoneText = styled.div`
  min-width: 0;
  flex: 1;

  .name {
    font-size: 13px;
    font-weight: 700;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .handle {
    font-size: 11px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const InfoFlat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  min-width: 0;
`

const InfoTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const BigAvatar = styled.div`
  width: 116px;
  height: 116px;
  border-radius: 50%;
  border: 4px solid ${props => props.theme.cardBackground};
  background-color: ${props => props.theme.borderLight};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  position: relative;
  flex-shrink: 0;
  margin-top: -80px;
  z-index: 3;

  &::after {
    content: '';
    position: absolute;
    bottom: 6px;
    right: 6px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #10b981;
    border: 3px solid ${props => props.theme.cardBackground};
  }
`

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`

const InfoName = styled.h1`
  margin: 0 0 4px 0;
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const InfoHandle = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 10px;
`

const InfoBio = styled.p`
  margin: 0 0 12px 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${props => props.theme.textPrimary};
`

const InfoMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 12px;

  & > div { display: flex; align-items: center; gap: 4px; }

  a {
    color: ${props => props.theme.buttonPrimary};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`

const InfoStats = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 8px;
`

const InfoStatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;

  .num {
    font-size: 20px;
    font-weight: 700;
    color: ${props => props.theme.textPrimary};
  }
  .label {
    font-size: 12px;
    color: ${props => props.theme.textSecondary};
    margin-top: 2px;
  }
`

const FlatActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;

  @media (max-width: 768px) {
    flex-direction: row;
    padding-top: 0;
  }
`

const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 140px;
  align-self: stretch;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
  }
`

const StackedActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover { background-color: ${props => props.theme.hoverBackground}; }

  &.primary {
    background-color: ${props => props.theme.buttonPrimary};
    color: #ffffff;
    border-color: ${props => props.theme.buttonPrimary};
    &:hover { background-color: ${props => props.theme.buttonPrimaryHover}; }
  }

  &.following {
    background-color: #10b981;
    color: #ffffff;
    border-color: #10b981;
    &:hover { background-color: #059669; }
  }

  @media (max-width: 768px) {
    flex: 1;
  }
`

const ProfileHeader = styled.div`
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    max-width: 420px;
    margin: 0 auto;
  }
`

const PhoneCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 20px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
  position: relative;
`

// === 새 PhoneCard 커버: 블러 배경 + 가운데 포스터 ===
const CoverWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 440px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  overflow: hidden;
`

const CoverBlurBg = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${props => props.$src ? `url(${props.$src})` : 'none'};
  background-size: cover;
  background-position: center;
  filter: blur(22px) saturate(1.1);
  transform: scale(1.2);
  opacity: 0.7;
`

const CoverPoster = styled.div`
  position: absolute;
  top: 18px;
  left: 18px;
  right: 18px;
  bottom: 56px;
  border-radius: 16px;
  overflow: hidden;
  background-image: ${props => props.$src ? `url(${props.$src})` : 'linear-gradient(135deg, #93a5fd 0%, #b78ad8 100%)'};
  background-size: cover;
  background-position: center;
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.28);
  z-index: 1;
`

const FloatingAvatar = styled.div`
  position: absolute;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  width: 84px;
  height: 84px;
  border-radius: 50%;
  border: 4px solid ${props => props.theme.cardBackground};
  background-color: ${props => props.theme.borderLight};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
`

const PhoneCardBody = styled.div`
  padding: 56px 22px 20px;
  text-align: center;
`

const PhoneCardName = styled.h1`
  margin: 0 0 4px 0;
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const PhoneCardHandle = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 14px;
`

const PhoneCardBio = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${props => props.theme.textPrimary};
  text-align: center;
`

const PhoneCardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 16px;

  & > div {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
  }

  & > div > span,
  & > div > a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  a {
    color: ${props => props.theme.buttonPrimary};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`

const PhoneCardEditBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 14px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  background-color: ${props => props.theme.hoverBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: ${props => props.theme.borderLight}; }
`

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const CoverImage = styled.div`
  width: 100%;
  aspect-ratio: 9 / 16;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CoverGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 50%,
    rgba(0, 0, 0, 0.55) 100%);
  pointer-events: none;
  z-index: 1;
`

const CoverOverlayInfo = styled.div`
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 20px;
  display: flex;
  align-items: flex-end;
  gap: 16px;
  z-index: 2;
`

const CoverAvatar = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.9);
  background-color: rgba(255, 255, 255, 0.15);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`

const CoverNameBlock = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: 4px;
`

const CoverName = styled.h1`
  margin: 0 0 2px 0;
  font-size: 26px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const CoverHandle = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
`

const ProfileActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const CoverEditButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background-color 0.15s ease;
  z-index: 2;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .spin {
    animation: ${spin} 0.9s linear infinite;
  }
`

const ProfileBody = styled.div`
  padding: 16px 18px 18px;
  position: relative;
`

const ActionsWidget = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadow};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ActionsWidgetTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const PrimaryActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 14px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.hoverBackground};
  }

  &.primary {
    background-color: ${props => props.theme.buttonPrimary};
    color: white;
    border-color: ${props => props.theme.buttonPrimary};

    &:hover { background-color: ${props => props.theme.buttonPrimaryHover}; }
  }

  &.following {
    background-color: #10b981;
    color: white;
    border-color: #10b981;

    &:hover { background-color: #059669; }
  }
`

const ProfileTopRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: -48px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: -40px;
  }
`

const AvatarContainer = styled.div`
  flex-shrink: 0;
`

const Avatar = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 4px solid ${props => props.theme.cardBackground};
  background-color: ${props => props.theme.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  color: ${props => props.theme.textMuted};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProfileActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding-bottom: 8px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 20px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.borderLight};
  }

  &.primary {
    background-color: ${props => props.theme.buttonPrimary};
    color: white;
    border-color: ${props => props.theme.buttonPrimary};

    &:hover {
      background-color: ${props => props.theme.buttonPrimaryHover};
    }
  }

  &.following {
    background-color: #10b981;
    color: white;
    border-color: #10b981;

    &:hover {
      background-color: #059669;
    }
  }
`

const UserDetails = styled.div`
  margin-top: 16px;

  @media (max-width: 768px) {
    text-align: center;
  }
`

const UserName = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 2px 0;
  color: ${props => props.theme.textPrimary};
`

const UserHandle = styled.div`
  font-size: 15px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 12px;
`

const Bio = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 12px;
  max-width: 600px;
`

const UserMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
  font-size: 13px;
  color: ${props => props.theme.textSecondary};

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  a {
    color: ${props => props.theme.buttonPrimary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`

const UserStats = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const StatItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const StatNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const StatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-top: 2px;
`

const ContentSection = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 16px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
`

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid ${props => props.theme.border};
`

const ContentTabs = styled.div`
  display: flex;
  gap: 4px;
`

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background-color: ${props => props.$active ? props.theme.buttonPrimary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.hoverBackground};
    color: ${props => props.$active ? 'white' : props.theme.textPrimary};
  }
`

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  overflow: hidden;
`

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
  border: none;
  background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.cardBackground};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.hoverBackground};
  }
`

const ContentGrid = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.textSecondary};

  .icon {
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: ${props => props.theme.textPrimary};
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.textSecondary};
`

// 날짜 포맷 함수
const formatJoinDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return `${date.getFullYear()}년 ${date.getMonth() + 1}월에 가입`;
};

export default function UserProfilePage() {
  const { username } = useParams()
  const { user: currentUser, isLoggedIn } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()

  const [profile, setProfile] = useState(null)
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [postsLoading, setPostsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("posts")
  const [viewMode, setViewMode] = useState("grid")
  const [isFollowing, setIsFollowing] = useState(false)
  const [stats, setStats] = useState({
    posts: 0,
    followers: 0,
    following: 0
  })
  const [showMatching, setShowMatching] = useState(false)
  const [coverUploading, setCoverUploading] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const avatarFileInputRef = useRef(null)
  const bannerFileInputRef = useRef(null)

  // 프로필 사진 변경
  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !currentUser) return
    try {
      const formData = new FormData()
      formData.append('image', file)
      const uploadRes = await fetch('/user/upload-avatar', { method: 'POST', body: formData, credentials: 'include' })
      const uploadData = await uploadRes.json()
      const avatarUrl = uploadData?.avatar_url || uploadData?.files?.[0]?.url
      if (!avatarUrl) { alert(uploadData?.message || '프로필 사진 업로드 실패'); return }
      const updateRes = await fetch('/auth/update-avatar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: currentUser.id, avatarUrl }),
      })
      const updateData = await updateRes.json()
      if (updateData.success) {
        setProfile(prev => ({ ...prev, avatar_url: avatarUrl }))
      } else {
        alert(updateData.message || '프로필 사진 적용 실패')
      }
    } catch (err) { alert(err.message) }
    finally { e.target.value = '' }
  }

  // 배너 사진 변경
  const handleBannerFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !currentUser) return
    try {
      const formData = new FormData()
      formData.append('files', file)
      const uploadRes = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=story_banner', { method: 'POST', body: formData })
      const uploadData = await uploadRes.json()
      const bannerUrl = uploadData?.files?.[0]?.url
      if (!bannerUrl) { alert('배너 이미지 업로드 실패'); return }
      const updateRes = await fetch('/auth/update-banner', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: currentUser.id, bannerUrl }),
      })
      const updateData = await updateRes.json()
      if (updateData.success) {
        setProfile(prev => ({ ...prev, banner_url: bannerUrl }))
      } else {
        alert(updateData.message || '배너 사진 적용 실패')
      }
    } catch (err) { alert(err.message) }
    finally { e.target.value = '' }
  }
  const coverFileInputRef = useRef(null)

  // 커버 크롭 모달 상태
  const [cropImage, setCropImage] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  // 프로필 데이터 로드
  useEffect(() => {
    fetchProfile()
  }, [username])

  // 커버 이미지 업로드 처리
  const handleCoverClick = () => {
    coverFileInputRef.current?.click()
  }

  // 파일 선택 → 크롭 모달 오픈
  const handleCoverFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기는 10MB 이하여야 합니다.')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      setCropImage(reader.result)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
    }
    reader.readAsDataURL(file)
    if (e.target) e.target.value = ''
  }

  const handleCropCancel = () => {
    setCropImage(null)
    setCroppedAreaPixels(null)
  }

  // 크롭 확정 → 잘린 이미지를 업로드
  const handleCropConfirm = async () => {
    if (!cropImage || !croppedAreaPixels) return

    setCoverUploading(true)
    try {
      const blob = await getCroppedBlob(cropImage, croppedAreaPixels)
      const formData = new FormData()
      formData.append('files', blob, 'cover.jpg')

      const uploadRes = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=story_antsnest', {
        method: 'POST',
        body: formData
      })
      const uploadData = await uploadRes.json()

      if (uploadData?.files?.[0]?.url) {
        const coverUrl = uploadData.files[0].url

        const updateRes = await fetch('/auth/update-cover', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ userId: currentUser.id, coverUrl })
        })
        const updateData = await updateRes.json()

        if (updateData.success) {
          setProfile(prev => ({ ...prev, cover_url: coverUrl }))
          setCropImage(null)
          setCroppedAreaPixels(null)
        } else {
          alert(updateData.message || '커버 이미지 업데이트에 실패했습니다.')
        }
      } else {
        alert('이미지 업로드에 실패했습니다.')
      }
    } catch (err) {
      console.error('커버 업로드 오류:', err)
      alert('커버 이미지 업로드 중 오류가 발생했습니다.')
    } finally {
      setCoverUploading(false)
    }
  }

  // 탭 변경시 콘텐츠 로드
  useEffect(() => {
    if (profile) {
      fetchContent()
    }
  }, [activeTab, profile])

  const fetchProfile = async () => {
    if (!username) return

    setLoading(true)
    try {
      const response = await fetch(`/user/profile/${username}`, {
        credentials: 'include'
      })

      const data = await response.json()
      if (data.success) {
        setProfile(data.user)
        setStats(data.stats || { posts: 0, followers: 0, following: 0 })
        setIsFollowing(data.isFollowing || false)
      } else {
        setProfile(null)
      }
    } catch (error) {
      console.error('프로필 로드 오류:', error)
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }

  const fetchContent = async () => {
    if (!profile) return

    setPostsLoading(true)
    try {
      let endpoint = ''
      switch (activeTab) {
        case 'posts':
          endpoint = `/post/user/${profile.id}?limit=20`
          break
        case 'likes':
          endpoint = `/post/liked/${profile.id}?limit=20`
          break
        case 'media':
          endpoint = `/post/user/${profile.id}?has_media=true&limit=20`
          break
        default:
          endpoint = `/post/user/${profile.id}?limit=20`
      }

      const response = await fetch(endpoint, {
        credentials: 'include'
      })

      const data = await response.json()
      if (data.success) {
        setPosts(data.posts || [])
      }
    } catch (error) {
      console.error('콘텐츠 로드 오류:', error)
      setPosts([])
    } finally {
      setPostsLoading(false)
    }
  }

  const handleFollow = async () => {
    if (!isLoggedIn || !profile) return

    try {
      // 서버: POST /user/follow/:userId — path param, toggle 동작
      const response = await fetch(`/user/follow/${profile.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })

      const data = await response.json()
      if (data.success) {
        setIsFollowing(data.isFollowing)
        setStats(prev => ({
          ...prev,
          followers: data.isFollowing ? prev.followers + 1 : Math.max(0, prev.followers - 1)
        }))
      } else {
        alert(data.message || '팔로우 처리 실패')
      }
    } catch (error) {
      console.error('팔로우 처리 오류:', error)
    }
  }

  const handleMessage = () => {
    navigate(`/messages?user=${profile.username}`)
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${profile.username}님의 프로필`,
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('프로필 링크가 클립보드에 복사되었습니다.')
    }
  }

  if (loading) {
    return (
      <Container>
        <LoadingState theme={theme}>프로필을 불러오는 중...</LoadingState>
      </Container>
    )
  }

  if (!profile) {
    return (
      <Container>
        <EmptyState theme={theme}>
          <User className="icon" size={64} />
          <h3>사용자를 찾을 수 없습니다</h3>
          <p>존재하지 않는 사용자이거나 삭제된 계정입니다.</p>
        </EmptyState>
      </Container>
    )
  }

  // currentUser.id가 SSO UUID이고 profile.id는 정수일 수 있어 username/sso_user_id로도 비교
  const isOwnProfile = !!currentUser && (
    String(currentUser.id) === String(profile.id) ||
    (currentUser.username && profile.username &&
      currentUser.username.toLowerCase() === profile.username.toLowerCase()) ||
    (profile.sso_user_id && String(currentUser.id) === String(profile.sso_user_id))
  )

  // 관리자 — admin@antsnest.co.kr는 어떤 프로필이든 ... 메뉴(관리) 사용 가능
  const isCurrentAdmin = !!currentUser && (
    String(currentUser.email || '').toLowerCase() === 'admin@antsnest.co.kr' ||
    String(currentUser.username || '').toLowerCase() === 'admin' ||
    currentUser.is_admin === true
  )
  const canShowMoreMenu = isOwnProfile || isCurrentAdmin

  return (
    <Container>
      {/* 커버 크롭 모달 */}
      {cropImage && (
        <CropOverlay onClick={handleCropCancel}>
          <CropModal onClick={(e) => e.stopPropagation()}>
            <CropModalHeader>
              <span>커버 영역 조정</span>
              <CropModalClose onClick={handleCropCancel} type="button" aria-label="닫기">
                <X size={18} />
              </CropModalClose>
            </CropModalHeader>
            <CropArea>
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={5 / 2}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </CropArea>
            <CropControls>
              <CropZoomLabel>확대</CropZoomLabel>
              <CropZoomSlider
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </CropControls>
            <CropActions>
              <CropCancelBtn onClick={handleCropCancel} type="button" disabled={coverUploading}>
                취소
              </CropCancelBtn>
              <CropConfirmBtn onClick={handleCropConfirm} type="button" disabled={coverUploading}>
                {coverUploading ? <Loader size={14} className="spin" /> : <Check size={14} />}
                <span>{coverUploading ? '업로드 중...' : '적용'}</span>
              </CropConfirmBtn>
            </CropActions>
          </CropModal>
        </CropOverlay>
      )}

      {/* 프로필 헤더 */}
      {/* 통합 카드: 배너 + 프로필 정보 */}
      <HeroWrap theme={theme}>
        <HeroBanner $cover={profile.banner_url}>
          {!profile.banner_url && (
            <HeroLogoOverlay>
              <div className="logo-text">
                <span>ANTSNEST</span>
                <small>BUILD TOGETHER, TOGETHER</small>
              </div>
            </HeroLogoOverlay>
          )}
          {isOwnProfile && (
            <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 5 }}>
              <BannerMoreBtn type="button" onClick={(e) => { e.stopPropagation(); setMoreMenuOpen(v => !v); }}>
                <MoreHorizontal size={18} />
              </BannerMoreBtn>
              {moreMenuOpen && (
                <>
                  <div onClick={() => setMoreMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                  <div
                    style={{
                      position: 'absolute', top: 40, right: 0, zIndex: 60,
                      background: '#fff', borderRadius: 10, minWidth: 200,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                      border: '1px solid #e5e7eb', overflow: 'hidden',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ProfileMoreMenuItem onClick={() => { setMoreMenuOpen(false); handleCoverClick(); }}>📷 배경사진 변경</ProfileMoreMenuItem>
                    <ProfileMoreMenuItem onClick={() => { setMoreMenuOpen(false); bannerFileInputRef.current?.click(); }}>🖼️ 배너사진 변경</ProfileMoreMenuItem>
                    <ProfileMoreMenuItem onClick={() => { setMoreMenuOpen(false); avatarFileInputRef.current?.click(); }}>👤 프로필 사진 변경</ProfileMoreMenuItem>
                  </div>
                </>
              )}
              <input ref={avatarFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarFileChange} />
              <input ref={bannerFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBannerFileChange} />
            </div>
          )}
        </HeroBanner>

        {/* 프로필 정보 행 (플랫): 부유 폰(모바일 배사) + 정보 + 액션 */}
        <ProfileFlat>
        <FloatingPhone $cover={profile.cover_url || profile.cover_image}>
          {!(profile.cover_url || profile.cover_image) && (
            <PhoneWatermark>
              <span className="text">ANTSNEST</span>
            </PhoneWatermark>
          )}
          <FloatingPhoneInner>
            <FloatingPhoneAvatar theme={theme} $avatar={profile.avatar_url}>
              {!profile.avatar_url && <User size={18} />}
            </FloatingPhoneAvatar>
            <FloatingPhoneText>
              <div className="name">{profile.full_name || profile.username}</div>
              <div className="handle">@{profile.username}</div>
            </FloatingPhoneText>
          </FloatingPhoneInner>
        </FloatingPhone>

        <InfoFlat>
          <InfoTopRow>
            <BigAvatar theme={theme} $avatar={profile.avatar_url}>
              {!profile.avatar_url && <User size={28} />}
            </BigAvatar>
            <div>
              <InfoName theme={theme} style={{ display: 'inline', marginRight: 8 }}>
                {profile.full_name || profile.username}
              </InfoName>
              <InfoHandle theme={theme} style={{ display: 'inline' }}>
                @{profile.username}
              </InfoHandle>
              {profile.bio && (
                <InfoBio theme={theme} style={{ marginTop: 6 }}>{profile.bio}</InfoBio>
              )}
            </div>
          </InfoTopRow>

          <InfoMeta theme={theme}>
            <div>
              <Calendar size={13} />
              <span>{formatJoinDate(profile.created_at)}</span>
            </div>
            {profile.email && (
              <div>
                <Mail size={13} />
                <span>{profile.email}</span>
              </div>
            )}
            {profile.location && (
              <div>
                <MapPin size={13} />
                <span>{profile.location}</span>
              </div>
            )}
            {profile.website && (
              <div>
                <LinkIcon size={13} />
                <a href={profile.website} target="_blank" rel="noopener noreferrer">
                  {profile.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
          </InfoMeta>

          <InfoStats>
            <InfoStatItem theme={theme}>
              <span className="num">{stats.posts}</span>
              <span className="label">게시글</span>
            </InfoStatItem>
            <InfoStatItem theme={theme}>
              <span className="num">{stats.following}</span>
              <span className="label">팔로잉</span>
            </InfoStatItem>
            <InfoStatItem theme={theme}>
              <span className="num">{stats.followers}</span>
              <span className="label">팔로워</span>
            </InfoStatItem>
          </InfoStats>
        </InfoFlat>

        <FlatActions>
          {isOwnProfile ? (
            <StackedActionButton theme={theme} className="primary" onClick={() => navigate('/settings')}>
              <Settings size={14} />
              프로필 편집
            </StackedActionButton>
          ) : (
            <>
              {isLoggedIn && (
                <>
                  <StackedActionButton
                    theme={theme}
                    className={isFollowing ? 'following' : 'primary'}
                    onClick={isCurrentAdmin && !isOwnProfile ? undefined : handleFollow}
                    disabled={isCurrentAdmin && !isOwnProfile}
                    style={isCurrentAdmin && !isOwnProfile ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'auto' } : {}}
                    title={isCurrentAdmin && !isOwnProfile ? '관리자 계정은 팔로우할 수 없습니다' : undefined}
                  >
                    <Users size={14} />
                    {isFollowing ? '팔로잉' : '팔로우'}
                  </StackedActionButton>
                  <StackedActionButton theme={theme} onClick={handleMessage}>
                    <MessageSquare size={14} />
                    메시지
                  </StackedActionButton>
                </>
              )}
              <StackedActionButton theme={theme} onClick={handleShare}>
                <Share size={14} />
                공유
              </StackedActionButton>
            </>
          )}
        </FlatActions>
        </ProfileFlat>
      </HeroWrap>

      {/* 콘텐츠 섹션 */}
      <ContentSection theme={theme}>
        <ContentHeader theme={theme}>
          <ContentTabs>
            <TabButton
              theme={theme}
              $active={activeTab === 'posts'}
              onClick={() => setActiveTab('posts')}
            >
              <Grid size={16} />
              게시물
            </TabButton>
            <TabButton
              theme={theme}
              $active={activeTab === 'media'}
              onClick={() => setActiveTab('media')}
            >
              <Camera size={16} />
              미디어
            </TabButton>
            {(isOwnProfile || isFollowing) && (
              <TabButton
                theme={theme}
                $active={activeTab === 'likes'}
                onClick={() => setActiveTab('likes')}
              >
                <Heart size={16} />
                좋아요
              </TabButton>
            )}
          </ContentTabs>

          <ViewToggle theme={theme}>
            <ViewButton
              theme={theme}
              $active={viewMode === 'grid'}
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </ViewButton>
            <ViewButton
              theme={theme}
              $active={viewMode === 'list'}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </ViewButton>
          </ViewToggle>
        </ContentHeader>

        {postsLoading ? (
          <LoadingState theme={theme}>콘텐츠를 불러오는 중...</LoadingState>
        ) : posts.length === 0 ? (
          <EmptyState theme={theme}>
            {activeTab === 'posts' && <Grid className="icon" size={48} />}
            {activeTab === 'media' && <Camera className="icon" size={48} />}
            {activeTab === 'likes' && <Heart className="icon" size={48} />}
            <h3>
              {activeTab === 'posts' && '게시물이 없습니다'}
              {activeTab === 'media' && '미디어가 없습니다'}
              {activeTab === 'likes' && '좋아요한 게시물이 없습니다'}
            </h3>
            <p>
              {isOwnProfile
                ? '첫 번째 게시물을 작성해보세요!'
                : `${profile.username}님의 콘텐츠를 기다려보세요.`
              }
            </p>
          </EmptyState>
        ) : viewMode === 'grid' ? (
          <ContentGrid>
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={{
                  id: post.id,
                  author: post.author_username,
                  authorAvatar: post.author_avatar,
                  timestamp: post.created_at,
                  images: post.images,
                  tags: post.hashtags || [],
                  description: post.content,
                  like_count: post.like_count,
                  comment_count: post.comment_count
                }}
                onPostDeleted={() => fetchContent()}
                onPostUpdated={() => fetchContent()}
              />
            ))}
          </ContentGrid>
        ) : (
          <ContentList>
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={{
                  id: post.id,
                  author: post.author_username,
                  authorAvatar: post.author_avatar,
                  timestamp: post.created_at,
                  images: post.images,
                  tags: post.hashtags || [],
                  description: post.content,
                  like_count: post.like_count,
                  comment_count: post.comment_count
                }}
                onPostDeleted={() => fetchContent()}
                onPostUpdated={() => fetchContent()}
              />
            ))}
          </ContentList>
        )}
      </ContentSection>

      {/* 스와이프 매칭 모달 */}
      {showMatching && (
        <SwipeMatching onClose={() => setShowMatching(false)} />
      )}
    </Container>
  )
}
